import { CHAT, CHAT_COMMANDS, chatCommand, CREATURE_BY_ID, itemDef, mandingaDef, attackName, SIMPATIA_BY_ID, SKILL_BY_ID, structureName, type ChatCommandName } from "@eldrun/content";
import type { SystemEvent } from "@eldrun/protocol";

/** O que o jogador digitou: fala, comando conhecido ou comando que não existe. */
export type ChatInput =
  | { type: "vazio" }
  | { type: "fala"; text: string }
  | { type: "comando"; name: ChatCommandName; arg: string }
  | { type: "desconhecido"; word: string };

export function parseChatInput(raw: string): ChatInput {
  const text = raw.trim();
  if (!text) return { type: "vazio" };
  if (!text.startsWith("/")) return { type: "fala", text };
  const [word = "", ...rest] = text.split(/\s+/);
  const name = chatCommand(word);
  return name ? { type: "comando", name, arg: rest.join(" ") } : { type: "desconhecido", word };
}

/** Comandos que recebem um apelido (o Tab completa o nome depois do espaço). */
const NAME_ARG = new Set<ChatCommandName>(["silenciar", "expulsar"]);

function commonPrefix(words: readonly string[]): string {
  if (words.length === 0) return "";
  let prefix = words[0]!;
  for (const w of words) while (!w.toLowerCase().startsWith(prefix.toLowerCase())) prefix = prefix.slice(0, -1);
  return prefix;
}

/** Como o jogador digita o comando (o `usage`), que pode não ser o id (`/presságio` é o `mandinga`). */
const typedName = (c: (typeof CHAT_COMMANDS)[number]): string => c.usage.split(" ")[0]!.slice(1);

/**
 * Autocompletar por Tab (doc 06): o nome do comando e, em `/silenciar` e `/expulsar`, o apelido.
 * Um só candidato completa com espaço; vários completam até o prefixo comum.
 */
export function completeChat(text: string, names: readonly string[]): string {
  const m = /^\/(\S*)$/.exec(text);
  if (m) {
    const all = CHAT_COMMANDS.flatMap((c) => [typedName(c), ...c.aliases]);
    const hits = [...new Set(all.filter((n) => n.startsWith(m[1]!.toLowerCase())))];
    if (hits.length === 1) return `/${hits[0]} `;
    return hits.length > 1 ? `/${commonPrefix(hits)}` : text;
  }
  const arg = /^\/(\S+)\s+(.*)$/.exec(text);
  const name = arg && chatCommand(arg[1]!);
  if (!arg || !name || !NAME_ARG.has(name)) return text;
  const typed = arg[2]!.toLowerCase();
  const hits = names.filter((n) => n.toLowerCase().startsWith(typed));
  if (hits.length === 1) return `/${arg[1]} ${hits[0]}`;
  return hits.length > 1 ? `/${arg[1]} ${commonPrefix(hits)}` : text;
}

/** Linhas do `/ajuda`. */
export function helpLines(): string[] {
  return CHAT_COMMANDS.map((c) => {
    const others = c.aliases.filter((a) => a !== typedName(c));
    return `${c.usage}: ${c.help}${others.length ? ` (também /${others.join(", /")})` : ""}`;
  });
}

const INVOKED: Record<string, string> = { chefe: `tocou um ${structureName("pedra-chefe")}: um chefe vem aí`, desafio: `começou um Desafio num ${structureName("pedra-encanto")}`, guardiao: "despertou um Guardião" };

/** Frase de uma mensagem de sistema (chave de tradução + parâmetros; doc 06). */
export function systemText(e: SystemEvent): string {
  const [a = "", b = "", c = ""] = e.params;
  const who = a || "Alguém";
  switch (e.key) {
    case "saiu":
      return `${who} saiu da partida.`;
    case "caiu-conexao":
      return `${who} caiu da conexão (tem 60 s para voltar).`;
    case "voltou":
      return `${who} voltou para a partida.`;
    case "morreu":
      return `${who} morreu.`;
    case "reviveu":
      // Sem quem ajudou: o convés da Jangada ou a própria volta paga sozinho (F54).
      if (!b) return c === "carranca" ? `${who} pagou a ${structureName("carranca")} e voltou.` : `${who} voltou.`;
      if (c === "fenix") return `${who} voltou no ${SKILL_BY_ID.get("circulo-da-fenix")?.name ?? "Círculo da Fênix"}, graças a ${b}.`;
      return `${who} voltou na ${structureName(c === "carranca" ? "carranca" : "cruz")}, graças a ${b}.`;
    case "caiu":
      return `${who} caiu! Um aliado tem 10 s para levantar.`;
    case "levantou":
      return `${who} foi levantado por ${b}.`;
    case "interrompeu":
      return `${who} interrompeu ${attackName(b)}!`;
    case "invocou":
      return `${who} ${INVOKED[b] ?? "invocou algo"}.`;
    case "drop-raro":
      return `${who} conseguiu ${SIMPATIA_BY_ID.get(b)?.name ?? itemDef(b)?.name ?? CREATURE_BY_ID.get(b)?.name ?? b}!`;
    case "votacao-aberta":
      return `${who} abriu uma votação para expulsar ${b}: /votar sim ou /votar nao.`;
    case "votacao-falhou":
      return `A votação para expulsar ${who} não passou.`;
    case "expulso-votacao":
      return `${who} foi expulso por votação.`;
    case "expulso-equipe":
      return `${who} foi removido da partida pela equipe do Eldrun.`;
    case "destravou":
      return "Você foi levado a um ponto seguro.";
  }
}

/** Descrição da Mandinga do Dia para o `/mandinga`. */
export function mandingaText(id: string): string {
  const def = id ? mandingaDef(id) : undefined;
  return def ? `Presságio do Dia: ${def.name} (${def.hint}).` : "Hoje não há Presságio.";
}

/** Linha do HUD da votação de expulsão em andamento, ou `undefined`. */
export function voteText(vote: { target: string; yes: number; needed: number; closesIn: number } | undefined, nameOf: (sid: string) => string, self: string): string | undefined {
  if (!vote?.target) return undefined;
  const about = vote.target === self ? "Votação para expulsar você" : `Expulsar ${nameOf(vote.target)}? /votar sim ou /votar nao`;
  return `${about} · ${vote.yes} de ${vote.needed} · ${vote.closesIn} s`;
}

export interface ChatLine {
  kind: "fala" | "sistema" | "local";
  text: string;
  /** Autor da fala (sessionId) e o apelido no momento. */
  from?: string;
  name?: string;
  /** Canal dos caídos na Treta. */
  dead?: boolean;
  /** Instante (ms) em que chegou: some depois de `CHAT.fade` s com o chat fechado. */
  at: number;
}

/** Histórico do chat no cliente, com o silenciar local (doc 06). */
export class ChatLog {
  readonly lines: ChatLine[] = [];
  private readonly muted = new Set<string>();

  push(line: ChatLine): boolean {
    if (line.kind === "fala" && line.from && this.muted.has(line.from)) return false;
    this.lines.push(line);
    if (this.lines.length > CHAT.history) this.lines.splice(0, this.lines.length - CHAT.history);
    return true;
  }

  /** Silencia ou volta a ouvir; devolve se ficou silenciado. */
  toggleMute(sessionId: string): boolean {
    if (this.muted.delete(sessionId)) return false;
    this.muted.add(sessionId);
    return true;
  }

  isMuted(sessionId: string): boolean {
    return this.muted.has(sessionId);
  }

  /** Linhas à vista: todas com o chat aberto; só as recentes com ele fechado. */
  visible(now: number, open: boolean): ChatLine[] {
    return open ? this.lines : this.lines.filter((l) => now - l.at < CHAT.fade * 1000);
  }
}
